import { 
    IDictionary,    
    FeatureTogglePoint,
    ClientFeature
} from 'util/domain';
import {   
    IToggleService,
    StaticToggleService
} from 'util/services/staticToggleService';
import { 
    PhoenixFeature,
    PhoenixFeatureStatus 
} from 'phoenix/domain';
import { 
    FeatureService, 
    AuthService 
} from 'phoenix/services'

const transformer = (feature:PhoenixFeatureStatus):ClientFeature => {
    let name = `${feature.FeatureNamespace}/${feature.FeatureName}`;
    let dateToggled = feature.DateToggled ? new Date(feature.DateToggled) : null;

    return {
        name,
        get enabled() {
            return feature.IsToggledOn;
        },
        dateToggled
    };
};


/**
 * A toggle service for consulting the Phoenix API for features 
 * in addition to local, static toggles.
 */
export class PhoenixToggleService implements IToggleService  {
    static readonly $inject = [
        '$q',
        'staticToggleService',
        'pxFeatureService',
        'pxAuthService'
    ];

    get features() {
        return this.staticToggleService.features;
    }

    constructor(
        private readonly $q: ng.IQService,
        private readonly staticToggleService:StaticToggleService,
        private readonly pxFeatureService:FeatureService,
        private readonly  pxAuthService:AuthService,
        preloadedFeatures:ClientFeature[] = []
    ) {
        preloadedFeatures.forEach(f => this.add(f));
    }

    public getFeature(fullyQualifiedFeature:string) {
        let [featureNS, featureName] = fullyQualifiedFeature.split(/[\/|.]/, 2);
        let staticFeature = this.staticToggleService.getFeature(fullyQualifiedFeature);
        let p:ng.IPromise<ClientFeature>;

        if(staticFeature){
            p = this.$q.resolve(staticFeature);
        }
        else if(featureNS && featureName) {
            p = this.pxFeatureService.getFeature(featureNS, featureName)        
                .then(feature => transformer(feature))
                .catch(() => null);
        }
        else {
            p = this.$q.resolve(null);
        }

        return p;
    }

    /**
     * Checks the Phoenix API to determine if the specified feature is enabled.
     * If the feature is specified locally, this will be take precedence
     * and the API will not be consulted. If no feature is found, the toggle will
     * be considered disabled.
     */
    public isEnabled(fullyQualifiedFeature:string):ng.IPromise<boolean>;
    public isEnabled(...features:string[]):ng.IPromise<IDictionary<boolean>>;
    public isEnabled(...featureNames:string[])
     {
        const isEnabled = (f?:ClientFeature) => f ? f.enabled : false;
        if(featureNames.length === 1){
            return this.getFeature(featureNames[0]).then(isEnabled);
        }
        else {
            let featureResults = featureNames.map(featName => this.getFeature(featName));
            return this.$q.all(featureResults)
                .then(features => features.reduce((dict, f) => {
                    dict[f.name] = isEnabled(f);
                    return dict;
                }, {} as IDictionary<boolean>));
        }            
    }

    public with(featureName:string) {
        // track last promise to sequentially invoke chained method calls
        let lastPromise:ng.IPromise<any> = this.$q.resolve();
        let featurePromise = this.isEnabled(featureName);            
        let togglePoint:FeatureTogglePoint = {
            on: callback => {
                lastPromise = lastPromise
                    .then(() => featurePromise)
                    .then(isEnabled => {
                        if(isEnabled){
                            callback();
                        }
                    });
                return togglePoint;
            }, 
            off: callback => {
                lastPromise = lastPromise
                    .then(() => featurePromise)
                    .then(isEnabled => {
                        if(!isEnabled){
                            callback();
                        }
                    })
                    .catch(callback);
                return togglePoint;  
            },
            either: callback => {
                lastPromise = lastPromise.then(callback);
                return togglePoint;
            }
        };

        return togglePoint;
    }

    public set(feature:string, enabled:boolean) {
        let { namespace, name } = this.breakUpFeatureName(feature);
        let p = this.$q.resolve();

        // API toggle
        if(namespace && name){
            // remove a static toggle if it exists so that we defer to the API
            this.staticToggleService.remove(feature);
            p = this.pxFeatureService.user.toggle(this.pxAuthService.user, namespace, name, enabled);
        }
        // web toggle
        else {
            this.staticToggleService.set(feature, enabled);
        }

        return p;
    }

    public add(feature:ClientFeature) {
        this.staticToggleService.add(feature);
        return this;
    }

    /**
    * Returns the provided object if the feature in question is turned on.
    * @param featureName 
    * @param object 
    */
    public if<T>(featureName:string, object:T) {
        return this.isEnabled(featureName)
            .then(isEnabled => {
                return isEnabled ? object : null;
            });
    }

    /**
     * Returns the provided object if the feature in question is turned off.
     * @param featureName 
     * @param object 
     */
    public not<T>(featureName:string, object:T) {
        return this.isEnabled(featureName)
            .then(isEnabled => {
                return !isEnabled ? object : null;
            });
    }

    private breakUpFeatureName(name:string){
        let pieces = name.split('/');
        return {
            namespace: pieces[0],
            name: pieces[1]
        };
    }
}

export class PhoenixToggleServiceProvider implements ng.IServiceProvider {
    private preloadedFeatures:ClientFeature[] = [];
    public readonly $get = [
        '$q',
        'staticToggleService',
        'pxFeatureService',
        'pxAuthService',
        ($q:ng.IQService, staticToggleService:StaticToggleService, pxFeatureService:FeatureService, pxAuthService:AuthService) => {
            let service = new PhoenixToggleService($q, staticToggleService, pxFeatureService, pxAuthService);
            this.preloadedFeatures.forEach(f => service.add(f));

            return service;
        }
    ];
    constructor() {
    }

    public addFeature(feature:ClientFeature){
        this.preloadedFeatures[feature.name] = feature;
        return this;
    }
};


// WEBPACK FOOTER //
// ./src/phoenix/services/phoenixToggleService.ts